'use client'

import { Box, Text } from '@mantine/core'
import Image from 'next/image'
import { useState } from 'react'

import type { HomePageV2FAQSection } from '@/services/cms/homePageV2'

import RichText from '../Richtext'

const mktSiteAssetsBaseUrl = 'https://mkt-site-asset.crypto.com/assets'

interface FAQProps {
  data: HomePageV2FAQSection
}

export default function FAQ({ data }: FAQProps) {
  const [openIndices, setOpenIndices] = useState<number[]>([])

  if (!data.isVisible) return null

  const toggleItem = (index: number) => {
    setOpenIndices(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index],
    )
  }

  return (
    <Box bg="#080D1B" py={64}>
      {data.title && (
        <Text
          fz={{ base: 18, lg: 21 }}
          lh="normal"
          fw={550}
          c="#F7F9FA"
          style={{ letterSpacing: '-0.09px' }}
          mb={{ base: 32, lg: 48 }}
        >
          {data.title}
        </Text>
      )}
      {data.list?.map((item, index) => {
        const isOpen = openIndices.includes(index)
        return (
          <Box key={item.id ?? item.faq.question} style={{ borderBottom: '1px solid #323C52' }}>
            <Box
              component="button"
              pt={{ base: index === 0 ? 0 : 12, lg: index === 0 ? 0 : 16 }}
              pb={{ base: 12, lg: 16 }}
              onClick={() => toggleItem(index)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Box
                fz={{ base: 18, lg: 21 }}
                lh="normal"
                fw={550}
                c="#F7F9FA"
                style={{ flex: 1, letterSpacing: '-0.09px' }}
              >
                {item.faq.question}
              </Box>
              {isOpen ? (
                <Image
                  alt="Minus Sign"
                  src={`${mktSiteAssetsBaseUrl}/cards/minus-sign.svg`}
                  width={20}
                  height={2}
                />
              ) : (
                <Image
                  alt="Plus Sign"
                  src={`${mktSiteAssetsBaseUrl}/cards/plus-sign.svg`}
                  width={24}
                  height={24}
                />
              )}
            </Box>
            <Box
              style={{
                display: 'grid',
                gridTemplateRows: isOpen ? '1fr' : '0fr',
                transition: 'grid-template-rows 300ms ease-in-out',
              }}
            >
              <Box style={{ overflow: 'hidden' }}>
                <Box fz={14} fw={400} lh="155%" c="#C9CFDD" pt={16} pb={24}>
                  <RichText data={item.faq.answer} />
                </Box>
              </Box>
            </Box>
          </Box>
        )
      })}
      {data.footnote && (
        <Box
          mt={32}
          fz={{ base: 18, lg: 21 }}
          lh="normal"
          fw={550}
          c="#F7F9FA"
          style={{ letterSpacing: '-0.09px' }}
        >
          <RichText data={data.footnote} />
        </Box>
      )}
    </Box>
  )
}
